import React, {useEffect} from 'react';
import {Helmet} from "react-helmet";
import {
  Link,
} from "react-router-dom";

import {connect} from 'react-redux';
import SeoHeaders from "../components/SeoHeaders";

function ResponsibleDisclosure() {
  return (
    <div className="relative py-16 bg-white overflow-hidden">
      <SeoHeaders
          title={"Responsible Disclosure Policy | Security | Buddy"}
          description={"Buddy's responsible disclosure policy for security researchers"}
      />
      <div className="hidden lg:block lg:absolute lg:inset-y-0 lg:h-full lg:w-full">
        <div className="relative h-full text-lg max-w-prose mx-auto">
          <svg className="absolute top-12 left-full transform translate-x-32" width="404" height="384" fill="none" viewBox="0 0 404 384">
            <defs>
              <pattern id="74b3fd99-0a6f-4271-bef2-e80eeafdf357" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                <rect x="0" y="0" width="4" height="4" className="text-gray-200" fill="currentColor" />
              </pattern>
            </defs>
            <rect width="404" height="384" fill="url(#74b3fd99-0a6f-4271-bef2-e80eeafdf357)" />
          </svg>
          <svg className="absolute top-1/2 right-full transform -translate-y-1/2 -translate-x-32" width="404" height="384" fill="none" viewBox="0 0 404 384">
            <defs>
              <pattern id="f210dbf6-a58d-4871-961e-36d5016a0f49" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                <rect x="0" y="0" width="4" height="4" className="text-gray-200" fill="currentColor" />
              </pattern>
            </defs>
            <rect width="404" height="384" fill="url(#f210dbf6-a58d-4871-961e-36d5016a0f49)" />
          </svg>
          <svg className="absolute bottom-12 left-full transform translate-x-32" width="404" height="384" fill="none" viewBox="0 0 404 384">
            <defs>
              <pattern id="d3eb07ae-5182-43e6-857d-35c643af9034" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                <rect x="0" y="0" width="4" height="4" className="text-gray-200" fill="currentColor" />
              </pattern>
            </defs>
            <rect width="404" height="384" fill="url(#d3eb07ae-5182-43e6-857d-35c643af9034)" />
          </svg>
        </div>
      </div>
      <div className="relative px-4 sm:px-6 lg:px-8">
        <div className="text-lg max-w-prose mx-auto mb-6">
          <p className="text-base text-center leading-6 text-orange-600 font-semibold tracking-wide uppercase">Security</p>
          <h1 className="mt-2 mb-8 text-3xl text-center leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl sm:leading-10">Buddy Ltd Responsible Disclosure Policy</h1>
        </div>
        <div className="prose prose-md text-gray-500 mx-auto">
          <p>At <strong>Buddy Ltd</strong>, we take the security of our systems and our customers' data very seriously. We welcome reports from security researchers who act in good faith and responsibly disclose potential vulnerabilities.</p>

          <h2>1. Purpose</h2>
          <p>This policy outlines how security researchers can report vulnerabilities to us, the rules of engagement for testing, and our commitments in handling these reports. It is designed to encourage responsible discovery while protecting the privacy and integrity of our systems and customers.</p>

          <h2>2. Scope</h2>
          <p>This policy applies to:</p>
          <ul>
            <li>All web applications and systems operated by Buddy Ltd under the <code>buddy.hr</code> domain and subdomains.</li>
            <li>Any associated services or systems specifically authorised in writing by Buddy Ltd.</li>
          </ul>
          <p>Systems and services operated by third parties are <strong>not in scope</strong>.</p>

          <h2>3. Prior Authorisation for Testing</h2>
          <p>Researchers <strong>must request written authorisation before conducting any active security testing</strong> that may affect Buddy systems or data.</p>
          <p>To request authorisation, email <a href="mailto:security@buddy.hr"><strong>security@buddy.hr</strong></a> with the following details:</p>
          <ul>
            <li>Targets or endpoints to be tested</li>
            <li>Proposed testing methods and tools</li>
            <li>Intended time window for testing</li>
            <li>Researcher or organisation name and contact information</li>
          </ul>
          <p>We will review and respond within <strong>5 business days</strong>, and if appropriate, issue written authorisation specifying the approved scope and rules. Authorisation will <strong>not be unreasonably withheld</strong> for legitimate, non-destructive security research conducted in good faith.</p>
          <p>Do <strong>not</strong> begin testing until you have received explicit written authorisation.</p>

          <h2>4. Reporting a Vulnerability</h2>
          <p>If you believe you have found a vulnerability:</p>
          <ol>
            <li>Email <a href="mailto:security@buddy.hr"><strong>security@buddy.hr</strong></a> with a clear description of the issue.</li>
            <li>Include steps to reproduce, affected systems or endpoints, and any relevant screenshots or proof-of-concept code.</li>
            <li>Avoid accessing, modifying, or exfiltrating personal data. Use minimal data to demonstrate impact.</li>
          </ol>
          <p>Please do <strong>not</strong> publicly disclose details until we have confirmed and remediated the issue.</p>

          <h2>5. Our Commitments</h2>
          <ul>
            <li>We will acknowledge your report within <strong>5 business days</strong>.</li>
            <li>We will investigate promptly and aim to resolve verified issues as quickly as possible.</li>
            <li>We will keep you informed once remediation is complete.</li>
            <li>We will not pursue legal action against researchers who act in good faith, within the authorised scope, and comply with this policy.</li>
          </ul>

          <h2>6. Guidelines for Testing</h2>
          <p>Researchers must:</p>
          <ul>
            <li>Avoid any action that could degrade, disrupt, or damage Buddy services.</li>
            <li>Not attempt denial-of-service, social engineering, phishing, or physical attacks.</li>
            <li>Not access or copy personal or confidential data.</li>
            <li>Stop testing immediately if you suspect you may have inadvertently accessed such data.</li>
          </ul>

          <h2>7. Rewards and Recognition</h2>
          <p>Buddy Ltd does <strong>not</strong> operate a paid bug bounty programme. While we do not offer financial rewards, we may acknowledge significant contributions publicly (with your consent) as a gesture of appreciation.</p>

          <h2>8. Safe Harbour</h2>
          <p>If you comply with this policy and act in good faith:</p>
          <ul>
            <li>Buddy Ltd will not initiate civil action or refer the matter for law enforcement investigation.</li>
            <li>Activities must remain within the authorised and defined scope.</li>
          </ul>
          <p>Actions outside these boundaries or involving unauthorised access, exfiltration, or disruption may be considered unlawful.</p>

          <h2>9. Contact</h2>
          <p>All vulnerability reports and authorisation requests should be sent to:</p>
          <p><strong>Email:</strong> <a href="mailto:security@buddy.hr">security@buddy.hr</a><br/>
          <strong>Preferred Language:</strong> English<br/>
          <strong>Response Window:</strong> Within 5 business days</p>
          <p>A copy of this policy can always be found at: <a href="https://buddy.hr/security">https://buddy.hr/security</a></p>
          <p>For automated discovery, Buddy maintains a <strong>security.txt</strong> file at:<br/>
          <code>https://buddy.hr/.well-known/security.txt</code></p>

          <p><em>Last updated: October 2025</em></p>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    // showSidebar: state.sidebar.show,
    // loggedIn: state.my.profile ? true : false,
  }
}

const mapDispatchToProps = dispatch => ({
  // myGetAgreements: () => dispatch(MYACTIONS.myGetAgreements()),
  // getProfile: () => dispatch(MYACTIONS.getProfile()),
  // isLoggedIn: () => dispatch(AUTHENTICATIONACTIONS.isLoggedIn()),
  // getDepartments: () => dispatch(TEAMACTIONS.getDepartments()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ResponsibleDisclosure)
